import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Platform } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  AgentMode,
  CreateWorkspaceCompanyInput,
  UpdateWorkspaceCompanyInput,
  WorkspaceSummary,
} from '@tabbio/types';
import { useAuth } from '../hooks/useAuth';
import { QUERY_KEYS } from '../lib/query-keys';
import { workspaceToMode } from '../lib/sidebar';
import {
  createWorkspaceCompany,
  fetchWorkspaces,
  setActiveWorkspace,
  updateWorkspaceCompany,
} from '../lib/workspaces-api';
import { markStartupMetric } from '../lib/startup-metrics';
import { readWebStorage, writeWebStorage } from '../lib/web-storage';
import { createSecureStoreKey } from '../lib/secure-store-key';
import { EMPLOYER_WORKSPACES_ENABLED } from '../lib/launch-scope';
import {
  FALLBACK_WORKSPACE,
  WorkspaceContext,
} from './WorkspaceProvider.shared';

const STORAGE_KEY_PREFIX = 'tabbio-workspace-id';

function getWebStorageKey(userId?: string | null) {
  return userId ? `${STORAGE_KEY_PREFIX}:${userId}` : STORAGE_KEY_PREFIX;
}

function getNativeStorageKey(userId?: string | null) {
  return userId
    ? createSecureStoreKey(STORAGE_KEY_PREFIX, userId)
    : STORAGE_KEY_PREFIX;
}

async function getStoredWorkspaceId(
  userId?: string | null,
): Promise<string | null> {
  if (Platform.OS === 'web') {
    return readWebStorage(getWebStorageKey(userId));
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const SecureStore = require('expo-secure-store');
    return SecureStore.getItemAsync(getNativeStorageKey(userId));
  } catch {
    return null;
  }
}

function storeWorkspaceId(userId: string | null | undefined, id: string) {
  if (Platform.OS === 'web') {
    writeWebStorage(getWebStorageKey(userId), id);
    return;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const SecureStore = require('expo-secure-store');
    void SecureStore.setItemAsync(getNativeStorageKey(userId), id);
  } catch {
    // noop
  }
}

// ─── Provider ────────────────────────────────────────────────────────

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isOnboardingCompleted } = useAuth();
  const [optimisticActiveId, setOptimisticActiveId] = useState<string | null>(
    null,
  );
  const [cachedWorkspaceId, setCachedWorkspaceId] = useState<string | null>(
    null,
  );
  const isEnabled =
    isAuthenticated && isOnboardingCompleted && EMPLOYER_WORKSPACES_ENABLED;
  const workspacesQueryKey = QUERY_KEYS.WORKSPACES(user?.id);

  const workspacesQuery = useQuery({
    queryKey: workspacesQueryKey,
    queryFn: fetchWorkspaces,
    enabled: isEnabled,
    staleTime: 30_000,
  });

  useEffect(() => {
    let isMounted = true;
    setCachedWorkspaceId(null);

    void getStoredWorkspaceId(user?.id).then((workspaceId) => {
      if (isMounted) {
        setCachedWorkspaceId(workspaceId);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (workspacesQuery.data?.activeWorkspaceId) {
      storeWorkspaceId(user?.id, workspacesQuery.data.activeWorkspaceId);
    }
  }, [user?.id, workspacesQuery.data?.activeWorkspaceId]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    if (workspacesQuery.data || workspacesQuery.error) {
      markStartupMetric('workspace_ready_ms');
    }
  }, [isEnabled, workspacesQuery.data, workspacesQuery.error]);

  const fallbackPersonalWorkspace = useMemo<WorkspaceSummary>(
    () => ({
      ...FALLBACK_WORKSPACE,
      label: t('labels.personal'),
      initial: t('labels.personal').charAt(0).toUpperCase() || 'P',
    }),
    [t],
  );

  const activeWorkspaceId =
    !EMPLOYER_WORKSPACES_ENABLED
      ? 'personal'
      : optimisticActiveId ||
        workspacesQuery.data?.activeWorkspaceId ||
        cachedWorkspaceId ||
        'personal';

  const fallbackCompanyWorkspace = useMemo<WorkspaceSummary | null>(() => {
    if (activeWorkspaceId === 'personal') {
      return null;
    }

    const companyLabel = t('nav.company');

    return {
      id: activeWorkspaceId,
      type: 'company',
      label: companyLabel,
      initial: companyLabel.charAt(0).toUpperCase() || 'C',
    };
  }, [activeWorkspaceId, t]);

  const switchMutation = useMutation({
    mutationFn: setActiveWorkspace,
    onSuccess: ({ activeWorkspaceId }) => {
      storeWorkspaceId(user?.id, activeWorkspaceId);
      queryClient.setQueryData(workspacesQueryKey, (previous: any) =>
        previous ? { ...previous, activeWorkspaceId } : previous,
      );
      queryClient.invalidateQueries({ queryKey: ['agent', 'threads'] });
    },
  });

  const createCompanyMutation = useMutation({
    mutationFn: createWorkspaceCompany,
    onSuccess: (result) => {
      // `activeWorkspaceId` is null when the create deliberately did not move
      // the owner's workspace (the partner API creates companies on their
      // behalf). This first-party path always moves it, but follow the server
      // rather than assume: the new company still joins the list either way.
      const nextActiveWorkspaceId = result.activeWorkspaceId;
      if (nextActiveWorkspaceId) {
        storeWorkspaceId(user?.id, nextActiveWorkspaceId);
      }
      queryClient.setQueryData(workspacesQueryKey, (previous: any) => {
        if (!previous) return previous;

        const nextWorkspaces = previous.workspaces.some(
          (workspace: WorkspaceSummary) => workspace.id === result.workspace.id,
        )
          ? previous.workspaces
          : [...previous.workspaces, result.workspace];

        return {
          ...previous,
          workspaces: nextWorkspaces,
          activeWorkspaceId:
            nextActiveWorkspaceId ?? previous.activeWorkspaceId,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['agent', 'threads'] });
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: ({
      companyId,
      input,
    }: {
      companyId: string;
      input: UpdateWorkspaceCompanyInput;
    }) => updateWorkspaceCompany(companyId, input),
    onSuccess: (result) => {
      queryClient.setQueryData(workspacesQueryKey, (previous: any) => {
        if (!previous) return previous;

        return {
          ...previous,
          workspaces: previous.workspaces.map((workspace: WorkspaceSummary) =>
            workspace.id === result.workspace.id ? result.workspace : workspace,
          ),
        };
      });
      // Identity/settings saves change the server-derived company checklist
      // (tagline/industry/website feed PRD §11 outcomes), which lives on the
      // builder payload — refetch it so the setup ring updates without a
      // reload.
      queryClient.invalidateQueries({
        queryKey: ['public-profile', 'builder', 'company'],
      });
    },
  });

  const workspaces = !EMPLOYER_WORKSPACES_ENABLED
    ? [fallbackPersonalWorkspace]
    : workspacesQuery.data?.workspaces?.length
      ? workspacesQuery.data.workspaces
      : !workspacesQuery.data && fallbackCompanyWorkspace
        ? [fallbackCompanyWorkspace, fallbackPersonalWorkspace]
        : [fallbackPersonalWorkspace];

  const activeWorkspace = !EMPLOYER_WORKSPACES_ENABLED
    ? fallbackPersonalWorkspace
    : workspaces.find((workspace) => workspace.id === activeWorkspaceId) ||
      (fallbackCompanyWorkspace?.id === activeWorkspaceId
        ? fallbackCompanyWorkspace
        : null) ||
      workspaces[0] ||
      fallbackPersonalWorkspace;

  const switchWorkspace = async (id: string) => {
    if (!EMPLOYER_WORKSPACES_ENABLED) {
      // Limited launch: company workspaces stay implemented but inaccessible.
      storeWorkspaceId(user?.id, 'personal');
      return fallbackPersonalWorkspace;
    }

    const targetWorkspace =
      workspaces.find((workspace) => workspace.id === id) ||
      fallbackPersonalWorkspace;

    setOptimisticActiveId(id);
    storeWorkspaceId(user?.id, id);

    try {
      await switchMutation.mutateAsync({ activeWorkspaceId: id });
      return targetWorkspace;
    } catch (error) {
      storeWorkspaceId(
        user?.id,
        workspacesQuery.data?.activeWorkspaceId || 'personal',
      );
      setOptimisticActiveId(null);
      throw error;
    } finally {
      setOptimisticActiveId(null);
    }
  };

  const createCompanyWorkspace = async (input: CreateWorkspaceCompanyInput) => {
    if (!EMPLOYER_WORKSPACES_ENABLED) {
      throw new Error('Company workspaces are not available in this launch.');
    }

    const result = await createCompanyMutation.mutateAsync(input);
    return result;
  };

  const updateCompanyWorkspaceAction = async (
    companyId: string,
    input: UpdateWorkspaceCompanyInput,
  ) => {
    if (!EMPLOYER_WORKSPACES_ENABLED) {
      throw new Error('Company workspaces are not available in this launch.');
    }

    const result = await updateCompanyMutation.mutateAsync({
      companyId,
      input,
    });
    return result;
  };

  const mode = !EMPLOYER_WORKSPACES_ENABLED
    ? ('seeker' as AgentMode)
    : (workspaceToMode(activeWorkspace) as AgentMode);
  const companyId =
    EMPLOYER_WORKSPACES_ENABLED && activeWorkspace.type === 'company'
      ? activeWorkspace.id
      : undefined;
  const isLoading =
    isEnabled && !cachedWorkspaceId && !workspacesQuery.data
      ? workspacesQuery.isLoading
      : false;
  const error =
    (workspacesQuery.error as Error | null) ||
    (switchMutation.error as Error | null) ||
    (createCompanyMutation.error as Error | null) ||
    (updateCompanyMutation.error as Error | null) ||
    null;

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        activeWorkspaceId,
        switchWorkspace,
        createCompanyWorkspace,
        updateCompanyWorkspace: updateCompanyWorkspaceAction,
        refetchWorkspaces: workspacesQuery.refetch,
        mode,
        companyId,
        isLoading,
        isSwitching: switchMutation.isPending,
        isCreatingCompany: createCompanyMutation.isPending,
        isUpdatingCompany: updateCompanyMutation.isPending,
        error,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
